import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Schools
export const schoolsApi = {
  list: () => api.get('/schools'),
  get: (id) => api.get(`/schools/${id}`),
  create: (data) => api.post('/schools', data),
  update: (id, data) => api.put(`/schools/${id}`, data),
  delete: (id) => api.delete(`/schools/${id}`),
  resetAlarms: (id) => api.post(`/schools/${id}/reset-alarms`),
};

// Rooms
export const roomsApi = {
  list: (schoolId) => api.get('/rooms', { params: { school_id: schoolId } }),
  get: (id) => api.get(`/rooms/${id}`),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  delete: (id) => api.delete(`/rooms/${id}`),
};

// Devices
export const devicesApi = {
  list: (params) => api.get('/devices', { params }),
  pending: () => api.get('/devices/pending'),
  link: (id, data) => api.post(`/devices/${id}/link`, data),
  get: (id) => api.get(`/devices/${id}`),
  getStatus: (id) => api.get(`/devices/${id}/status`),
  getEvents: (id, limit = 50) => api.get(`/devices/${id}/events`, { params: { limit } }),
  getSparkline: (id, minutes = 10) => api.get(`/devices/${id}/sparkline`, { params: { minutes } }),
  getTelemetry: (id, minutes = 60) => api.get(`/devices/${id}/telemetry`, { params: { minutes } }),
  create: (data) => api.post('/devices', data),
  createPlaceholder: (data) => api.post('/devices/placeholder', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  delete: (id) => api.delete(`/devices/${id}`),
  testAlarm: (id) => api.post(`/devices/${id}/test-alarm`),
  move: (id, roomId) => api.post(`/devices/${id}/move`, { room_id: roomId }),
  resetCalibration: (id) => api.post(`/devices/${id}/reset-calibration`),
  labelSizes: () => api.get('/devices/packaging-labels/sizes'),
  packagingPreview: (params) => api.get('/devices/packaging-labels/preview', { params, responseType: 'blob' }),
  packagingLabels: (data) => api.post('/devices/packaging-labels', data, { responseType: 'blob' }),
};

// Unlinked sensors (Okopplade sensorer)
export const unlinkedApi = {
  list: () => api.get('/unlinked-sensors'),
  roomSlots: (roomId) => api.get('/unlinked-sensors/room-slots', { params: { room_id: roomId } }),
  link: (id, data) => api.post(`/unlinked-sensors/${id}/link`, data),
  delete: (id) => api.delete(`/unlinked-sensors/${id}`),
};

// Network document (Nätverksunderlag) export
export const networkDocApi = {
  preview: (schoolId) => api.get('/network-document/preview', { params: { school_id: schoolId } }),
  exportDoc: (data) => api.post('/network-document/export', data, { responseType: 'blob' }),
};

// Events
export const eventsApi = {
  list: (params) => api.get('/events', { params }),
  acknowledge: (id, data) => api.post(`/events/${id}/acknowledge`, data),
  markFalsePositive: (id, data) => api.post(`/events/${id}/false-positive`, data),
  markSpray: (id) => api.post(`/events/${id}/mark-spray`),
  confirmVape: (id) => api.post(`/events/${id}/confirm-vape`),
  nothingFound: (id) => api.post(`/events/${id}/nothing-found`),
  resolve: (id, data) => api.post(`/events/${id}/resolve`, data),
};

// Alarm Profiles
export const alarmProfilesApi = {
  list: () => api.get('/alarm-profiles'),
  create: (data) => api.post('/alarm-profiles', data),
  update: (id, data) => api.put(`/alarm-profiles/${id}`, data),
  delete: (id) => api.delete(`/alarm-profiles/${id}`),
  createPredefined: () => api.post('/alarm-profiles/create-predefined'),
};

// Notification Rules
export const notificationRulesApi = {
  list: (schoolId) => api.get('/notification-rules', { params: { school_id: schoolId } }),
  create: (data) => api.post('/notification-rules', data),
  update: (id, data) => api.put(`/notification-rules/${id}`, data),
  delete: (id) => api.delete(`/notification-rules/${id}`),
  sendTest: (data) => api.post('/notifications/test', data),
};

// Onboarding checklist
export const onboardingChecklistApi = {
  get: (schoolId) => api.get('/onboarding/checklist', { params: schoolId ? { school_id: schoolId } : {} }),
  dismiss: (schoolId) => api.post('/onboarding/dismiss', { school_id: schoolId }),
  reset: (schoolId) => api.post('/onboarding/reset', { school_id: schoolId }),
};

// Users
export const usersApi = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Settings
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  sendWeeklyReport: () => api.post('/reports/send-weekly'),
};

// Analytics
export const analyticsApi = {
  eventsByDay: (params) => api.get('/analytics/events-by-day', { params }),
  eventsBySchool: (params) => api.get('/analytics/events-by-school', { params }),
  eventsByRoom: (params) => api.get('/analytics/events-by-room', { params }),
  hourlyDistribution: (params) => api.get('/analytics/hourly-distribution', { params }),
  airQuality: (params) => api.get('/analytics/air-quality', { params }),
  heatmap: (params) => api.get('/analytics/heatmap', { params }),
  tamperCheck: () => api.get('/analytics/tamper-check'),
  calibration: () => api.get('/analytics/calibration'),
  myAccuracy: () => api.get('/analytics/calibration/mine'),
  recalibrate: () => api.post('/analytics/calibration/recalibrate'),
  exportEvents: (params) => api.get('/analytics/export-events', { params, responseType: 'blob' }),
  exportPdf: (params) => api.get('/analytics/export-pdf', { params, responseType: 'blob' }),
};

// Seed demo data
// Seed endpoint removed

// Kommun
export const kommunApi = {
  dashboard: () => api.get('/kommun/dashboard'),
  schools: () => api.get('/kommun/schools'),
  trends: () => api.get('/kommun/trends'),
  reportPdf: () => api.get('/kommun/report/pdf', { responseType: 'blob' }),
};

// Incidents
export const incidentsApi = {
  list: (params) => api.get('/incidents', { params }),
  get: (id) => api.get(`/incidents/${id}`),
  assign: (id, data) => api.post(`/incidents/${id}/assign`, data),
  update: (id, data) => api.post(`/incidents/${id}/update`, data),
  close: (id, data) => api.post(`/incidents/${id}/close`, data),
};

// Knowledge Base
export const academyApi = {
  content: () => api.get('/academy/content'),
  progress: () => api.get('/academy/progress'),
  setProgress: (item_id, done) => api.put('/academy/progress', { item_id, done }),
  pdf: (slug, download = false) => api.get(`/academy/pdf/${slug}`, { params: { download }, responseType: 'blob' }),
  certificate: () => api.get('/academy/certificate', { responseType: 'blob' }),
  verifyCertificate: (number) => api.get(`/academy/certificate/verify/${encodeURIComponent(number)}`),
  team: () => api.get('/academy/team'),
};

export const academyMediaUrl = (src) => `${API_URL}/api/academy/media/${src}.png`;

export const knowledgeApi = {
  categories: () => api.get('/knowledge/categories'),
  documents: (params) => api.get('/knowledge/documents', { params }),
  document: (slug) => api.get(`/knowledge/documents/${slug}`),
  recommended: () => api.get('/knowledge/recommended'),
  downloadPdf: (slug, params) => api.get(`/knowledge/pdf/${slug}`, { params, responseType: 'blob' }),
  // Admin
  adminDocuments: () => api.get('/knowledge/admin/documents'),
  createDocument: (data) => api.post('/knowledge/admin/documents', data),
  updateDocument: (id, data) => api.put(`/knowledge/admin/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/knowledge/admin/documents/${id}`),
};

// Onboarding Flow
export const onboardingFlowApi = {
  // Public (no auth needed - uses raw axios)
  getForm: (token) => api.get(`/onboarding/form/${token}`),
  submitForm: (token, data) => api.post(`/onboarding/form/${token}/submit`, data),
  // Admin
  createLink: (data) => api.post('/onboarding/create-link', data),
  listSubmissions: () => api.get('/onboarding/submissions'),
  getSubmission: (id) => api.get(`/onboarding/submissions/${id}`),
  submissionPdf: (id) => api.get(`/onboarding/submissions/${id}/pdf`, { responseType: 'blob' }),
  updateSubmission: (id, data) => api.put(`/onboarding/submissions/${id}`, data),
  deleteSubmission: (id) => api.delete(`/onboarding/submissions/${id}`),
  activateSubmission: (id, data) => api.post(`/onboarding/submissions/${id}/activate`, data),
  createAlarmRules: (id) => api.post(`/onboarding/submissions/${id}/create-alarm-rules`),
};

export default api;
